import { pushContent } from '@bb/contracts';
import type { PushPlatform } from './pushDraft.ts';

/**
 * Lengths of push texts as devices count them (BB-73): graphemes — an emoji with modifiers or a flag is one character —
 * against the recommended limits of each surface (`PUSH_LENGTH_GUIDANCE`, Braze push docs). The same counting as
 * `@bb/domain` `length-guidance.ts`, which the dashboard does not depend on.
 */
const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

export const graphemesOf = (text: string): string[] => Array.from(segmenter.segment(text), (s) => s.segment);
export const graphemeCount = (text: string): number => graphemesOf(text).length;

/** `text` cut to `limit` graphemes with an ellipsis in the last place; untouched when it fits. */
export function truncateGraphemes(text: string, limit: number): { text: string; truncated: boolean } {
  const parts = graphemesOf(text);
  if (parts.length <= limit) return { text, truncated: false };
  return { text: `${parts.slice(0, Math.max(limit - 1, 0)).join('').trimEnd()}…`, truncated: true };
}

/** Surfaces of the guidance table. */
export type GuidanceSurface = (typeof pushContent.PUSH_LENGTH_GUIDANCE)[number]['surface'];

/** Notification states the preview draws. */
export type IosSurface = 'lock_screen' | 'banner' | 'notification_center';
export type AndroidSurface = 'collapsed' | 'expanded' | 'lock_screen';
export type PreviewSurface = IosSurface | AndroidSurface;
export type PreviewDevice = 'phone' | 'tablet';
export type PreviewTheme = 'light' | 'dark';

export const PREVIEW_SURFACES: { ios: Array<{ value: IosSurface; label: string }>; android: Array<{ value: AndroidSurface; label: string }> } = {
  ios: [
    { value: 'lock_screen', label: 'Lock screen' },
    { value: 'banner', label: 'Banner' },
    { value: 'notification_center', label: 'Notification Center' },
  ],
  android: [
    { value: 'collapsed', label: 'Collapsed' },
    { value: 'expanded', label: 'Expanded' },
    { value: 'lock_screen', label: 'Lock screen' },
  ],
};

export const SURFACE_LABELS: Record<PushPlatform, Record<string, string>> = {
  ios: { lock_screen: 'iOS lock screen', banner: 'iOS banner', notification_center: 'iOS Notification Center' },
  android: { lock_screen: 'Android lock screen', drawer_expanded: 'Android expanded drawer', collapsed: 'Android collapsed', expanded: 'Android expanded' },
};

/**
 * The guidance row a preview state follows: the Notification Center lays a notification out like the lock screen; a
 * collapsed Android notification shows one line of text like the lock screen; expanded is the expanded drawer.
 */
export function guidanceSurfaceOf(platform: PushPlatform, surface: PreviewSurface): GuidanceSurface {
  if (platform === 'ios') return surface === 'banner' ? 'banner' : 'lock_screen';
  return surface === 'expanded' ? 'drawer_expanded' : 'lock_screen';
}

export interface LengthLimit {
  platform: PushPlatform;
  surface: GuidanceSurface;
  label: string;
  limit: number;
  exceeded: boolean;
}

/** The body's recommended limits on each surface of the platforms, with or without an image. */
export function lengthLimits(count: number, platforms: readonly PushPlatform[], withImage: boolean): LengthLimit[] {
  return pushContent.PUSH_LENGTH_GUIDANCE.filter((g) => g.field === 'body' && platforms.includes(g.platform)).map((g) => {
    const limit = withImage ? g.withImage : g.graphemes;
    return { platform: g.platform, surface: g.surface, label: SURFACE_LABELS[g.platform][g.surface] ?? g.surface, limit, exceeded: count > limit };
  });
}

/** How many graphemes of the body a preview state shows before cutting it. */
export function bodyLimitOf(platform: PushPlatform, surface: PreviewSurface, withImage: boolean): number {
  const g = pushContent.PUSH_LENGTH_GUIDANCE.find((row) => row.platform === platform && row.field === 'body' && row.surface === guidanceSurfaceOf(platform, surface));
  return g ? (withImage ? g.withImage : g.graphemes) : Number.POSITIVE_INFINITY;
}
